import * as React from 'react'
import styled from 'styled-components'
import { IconName, ThemeName } from '../../../lib/constants'
import { NavigationDropdownTextList } from '../../Navigation/NavigationDropdownTextList'
import { NavigationDropdownLogos } from '../../Navigation/NavigationDropdownLogos'
import { Group } from './Group'
import { GroupHeader } from './GroupHeader'
import { GroupItem } from './GroupItem'
import { SidebarItem } from '../NavigationSidebarItem'
import CustomLink from '../../CustomLink'
import { useI18n } from '../../../lib/context/i18n'
import { Wizzard } from '../../OnboardingWizzard/Wizzard'
import { Button } from '../../UI/Button'

const footerComponents = {
  NavigationDropdownTextList,
  NavigationDropdownLogos,
}

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white[1]};
  border-radius: 1.5rem;
  box-shadow: 0px 20px 250px rgba(0, 0, 0, 0.06), 0px 12.3603px 90.1397px rgba(0, 0, 0, 0.0325794),
    0px 5.13384px 37.4394px rgba(0, 0, 0, 0.025), 0px 1.85681px 13.5411px rgba(0, 0, 0, 0.0174206);
  overflow: hidden;
  position: relative;
`

const CardInner = styled.div`
  display: flex;
  width: 100%
  max-width: 64rem;
  min-width: 8rem;
`

export const SidebarGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`


const Sidebar = styled.div`
  background-color: ${({ theme }) => theme.colors.black[7]};
  padding: 1rem;
  width: 100%;
  min-width: 9.5rem;
`

const Groups = styled.nav<{
  columns: number
}>`
  display: flex;
  gap: 2rem;
  padding: 1rem;
`

const Footer = styled.nav`
  padding: 30px 40px;
  background-color: ${({ theme }) => theme.colors.bluetiful[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.black[6]};
`

const FooterTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 110%;
`


const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1.5rem;
`

export type DropdownGroup = {
  label: string
  subTitle: string
  theme?: ThemeName
  items: Array<{
    label: string
    description?: string
    to: string
    icon: IconName
  }>
}

export type SidebarCustomerStory = {
  description: string
  logoSrc: string
  theme: ThemeName
  to: string
  type: 'customer-story'
}

export type SidebarCTAButton = {
  icon: IconName
  label: string
  theme: ThemeName
  title: string
  to: string
  type: 'cta-button'
}

export type SidebarGroupItem = {
  label: string
  to: string
}

export interface DropdownProps {
  data: any
}

const SidebarTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 110%;
  color: ${({ theme }) => theme.colors.black[4]};
  margin-bottom: 0.5rem;
  padding: 0.15rem 0.4rem;
`

const renderComponents = (components: Record<any, any>, items: Array<any>) => {
  return items.map((item, index) => {
    if (!!components[item.component]) {
      const Component = components[item.component]
      return <Component key={`dropdown-item-${index}`} {...item} />
    }
  })
}

const GroupHeaderWrapper = ({ group }: { group: any }) => {
  const { getTranslatedUrlFromLink } = useI18n()
  const isLink = !!group.link
  const linkUrl = group.link ? getTranslatedUrlFromLink(group.link) : undefined
  const isExternal = linkUrl && !linkUrl.startsWith('/')

  return (
    <GroupHeader
      title={group.title}
      subline={group.description}
      as={isLink ? CustomLink : undefined}
      href={isExternal ? linkUrl : undefined}
      to={!isExternal ? linkUrl : undefined}
    />
  )
}

export const Dropdown = ({ data }: DropdownProps) => {
  const columns = data.groups.length
  const { getTranslatedUrlFromLink } = useI18n()

  return (
    <Card>
      <CardInner>
        <Groups columns={columns}>
          {data.groups.map((group, groupIndex) => (
            <Group
              theme={`theme-${group.theme || 'azure'}` as ThemeName}
              key={`dropdown_group_${groupIndex}`}
            >
              <GroupHeaderWrapper group={group} />

              {group.items.map((item, itemIndex) => {
                if (item.component === 'LinkWithIcon') {
                  return (
                    <GroupItem
                      key={`${groupIndex}-${itemIndex}`}
                      label={item.label}
                      subline={item.description}
                      to={getTranslatedUrlFromLink(item.link)}
                      icon={item.icon}
                    />
                  )
                } else if (item.component === 'Button') {
                  return (
                    <ButtonWrapper key={`${groupIndex}-${itemIndex}`}>
                      <Button
                        key={`${groupIndex}-${itemIndex}`}
                        label={item.button_name}
                        title={item.button_name}
                        to={getTranslatedUrlFromLink(item.link)}
                        icon={item.icon}
                        as={CustomLink}
                        iconRight="arrow-right"
                        $size="small"
                        $inverted={true}
                        $ignoreTheme={true}
                        $variant="link"
                      />
                    </ButtonWrapper>
                  )
                }
              })}
            </Group>
          ))}
        </Groups>

        {data.sidebar_items?.length > 0 && (
          <Sidebar>
            <SidebarTitle>
              {data.sidebar_title}
            </SidebarTitle>
            <SidebarGroups>
              {data.sidebar_items.map((item, index) => (
                <SidebarItem
                  key={`sidebar-${item._uid || index}`}
                  item={item}
                />
              ))}
            </SidebarGroups>
          </Sidebar>
        )}
      </CardInner>
    </Card>
  )
}
